// 스폰 스케줄러 - 타이밍과 패턴을 관리
class SpawnScheduler {
  constructor(spawnManager, game = null) {
    this.spawnManager = spawnManager;
    this.game = game; // 게임 인스턴스 참조 (UI 매니저 접근용)

    // 스케줄된 스폰 이벤트들
    this.scheduledEvents = [];

    // 현재 게임 시간
    this.gameTime = 0;

    // 스케줄러 활성화 상태
    this.isActive = false;

    // 스테이지 시스템
    this.currentStage = 0; // 현재 스테이지 (0부터 시작)
    this.stageStartTime = 0; // 현재 스테이지 시작 시간
    this.isBossWave = false; // 현재 보스 웨이브인지 여부
    this.currentBoss = null; // 현재 보스 참조
  }

  // 스케줄러 시작
  start() {
    this.isActive = true;
    this.gameTime = 0;
    this.currentStage = 0;
    this.stageStartTime = 0;
    this.isBossWave = false;
    this.currentBoss = null;
    this.currentBosses = []; // 여러 보스 지원을 위한 배열 초기화
    // 한국어 주석: 기존 스케줄된 이벤트들도 모두 제거
    this.scheduledEvents = [];
    Utils.log("스폰 스케줄러가 시작되었습니다! (스테이지 1)");
  }

  // 스케줄러 정지
  stop() {
    this.isActive = false;
    Utils.log("스폰 스케줄러가 정지되었습니다!");
  }

  // 스케줄러 재개 (일시정지 후 재시작)
  resume() {
    this.isActive = true;
    Utils.log("스폰 스케줄러가 재개되었습니다!");
  }

  // 스케줄러 업데이트 (매 프레임 호출)
  update(delta) {
    if (!this.isActive) return;

    this.gameTime += delta;

    // 보스 처치 확인
    this.checkBossDeath();

    // 실행할 스폰 이벤트 확인 (정렬된 순서로 처리하여 카운트다운 등이 올바른 순서로 실행되도록 함)
    const eventsToExecute = this.scheduledEvents
      .filter((event) => this.gameTime >= event.triggerTime)
      .sort((a, b) => a.triggerTime - b.triggerTime); // 트리거 시간 순으로 정렬

    // 정렬된 이벤트들을 순서대로 실행
    for (const event of eventsToExecute) {
      const index = this.scheduledEvents.indexOf(event);
      if (index === -1) continue; // 이미 제거된 이벤트는 스킵

      // 스폰 이벤트 실행
      this.executeSpawnEvent(event);

      // 반복 이벤트가 아니면 제거
      if (!event.repeat) {
        this.scheduledEvents.splice(index, 1);
      } else {
        // duration이 설정되어 있고 시간이 지났으면 제거
        if (event.endTime && this.gameTime >= event.endTime) {
          Utils.log(
            `반복 스폰 이벤트 종료: ${event.enemyType} (duration: ${event.duration}초)`
          );
          this.scheduledEvents.splice(index, 1);
        } else {
          // 반복 이벤트면 다음 실행 시간 설정
          event.triggerTime = this.gameTime + event.interval;
        }
      }
    }
  }

  // 스폰 이벤트 실행
  async executeSpawnEvent(event) {
    try {
      // 보스 카운트다운 이벤트 처리
      if (event.pattern === "boss_countdown" && event.countdown !== undefined) {
        Utils.log(`보스 카운트다운 ${event.countdown} 표시!`);
        const gameInstance = this.game || window.game;
        if (gameInstance && gameInstance.uiManager) {
          gameInstance.uiManager.showBossCountdown(event.countdown);
        }
        return;
      }

      // 한국어 주석: 보스 스폰 이벤트라면 먼저 모든 적을 폭탄 효과로 제거
      if (
        event.enemyType === "WARRIOR" &&
        (event.pattern === "random" || event.pattern === "boss")
      ) {
        // 이미 보스가 있으면 새로 스폰하지 않음 (중복 스폰 방지)
        if (
          this.isBossWave &&
          (this.currentBosses?.length > 0 || this.currentBoss)
        ) {
          Utils.log("이미 보스가 있어서 새 보스 스폰을 건너뜁니다.", "warn");
          return;
        }

        Utils.log("보스 등장 전 모든 적 제거!");

        // 한국어 주석: 보스 등장 시 진행 중인 모든 반복 스폰 이벤트 취소 (고급 웨이브 등)
        this.cancelRepeatingSpawns();

        this.clearAllEnemiesWithBomb();

        // 폭탄 효과 후 잠시 대기 (0.5초)
        // await new Promise((resolve) => setTimeout(resolve, 500));
      }

      Utils.log(
        `스폰 이벤트 실행: ${event.pattern} - ${
          event.enemyType
        } (시간: ${this.gameTime.toFixed(1)}초)`
      );

      const result = await this.spawnManager.executeSpawnPattern(
        event.pattern,
        event.enemyType,
        ...event.args
      );

      // 보스 패턴의 경우 여러 보스가 배열로 반환될 수 있음
      // 모든 보스를 등록
      if (event.pattern === "boss" && Array.isArray(result)) {
        result.forEach((boss) => {
          if (boss && this.registerBoss) {
            this.registerBoss(boss);
          }
        });
      } else if (event.pattern === "boss" && result) {
        // 단일 보스도 배열이 아닐 수 있음
        if (this.registerBoss) {
          this.registerBoss(result);
        }
      }

      if (event.onComplete) {
        event.onComplete(result);
      }
    } catch (error) {
      Utils.log(`스폰 이벤트 실행 실패: ${error.message}`, "error");
    }
  }

  // 모든 적을 폭탄 효과로 제거 (Bomb 아이템의 applyEffect와 동일한 로직 사용)
  clearAllEnemiesWithBomb() {
    if (!this.spawnManager || !this.spawnManager.enemies) return;

    const enemies = this.spawnManager.enemies;

    if (enemies.length === 0) {
      Utils.log("제거할 적이 없습니다.");
      return;
    }

    Utils.log(`폭탄 효과로 ${enemies.length}마리의 적 제거 시작!`);

    // 폭탄 화면 효과 실행 (흰색 화면 FadeOut)
    if (window.game && window.game.uiManager) {
      window.game.uiManager.playBombScreenEffect();
    }

    let destroyedCount = 0;

    // 보스는 제외하고 일반 적만 제거 (보스 아이템 드롭 버그 방지)
    const bossEnemyTypes =
      this.currentBosses?.map((b) => b?.enemyType).filter(Boolean) || [];
    if (
      this.currentBoss?.enemyType &&
      !bossEnemyTypes.includes(this.currentBoss.enemyType)
    ) {
      bossEnemyTypes.push(this.currentBoss.enemyType);
    }

    // 모든 적에게 체력만큼 데미지를 주어 즉시 죽임 (보스 제외)
    enemies.forEach((enemy) => {
      // 보스는 건너뛰기 (보스 아이템 드롭 버그 방지)
      if (enemy && enemy.enemyType === "WARRIOR") {
        return;
      }

      if (enemy && enemy.isAlive) {
        enemy.takeDamage(enemy.health);
        destroyedCount++;
      }
    });

    // 한국어 주석: 플레이어의 적 목록도 업데이트 (있는 경우)
    if (
      this.spawnManager.player &&
      this.spawnManager.player.enemies &&
      Array.isArray(this.spawnManager.player.enemies)
    ) {
      this.spawnManager.player.enemies.forEach((enemy) => {
        if (enemy && enemy.isAlive) {
          enemy.takeDamage(enemy.health);
        }
      });
    }

    Utils.log(`폭탄 효과로 ${destroyedCount}마리의 적이 제거되었습니다!`);
  }

  // 단일 스폰 이벤트 스케줄링
  scheduleSpawn(delaySeconds, pattern, enemyType, ...args) {
    const event = {
      triggerTime: this.gameTime + delaySeconds,
      pattern: pattern,
      enemyType: enemyType,
      args: args,
      repeat: false,
      id: this.generateEventId(),
    };

    this.scheduledEvents.push(event);
    Utils.log(`스폰 스케줄 등록: ${delaySeconds}초 후 ${pattern} ${enemyType}`);

    return event.id;
  }

  // 반복 스폰 이벤트 스케줄링
  scheduleRepeatingSpawn(
    delaySeconds,
    intervalSeconds,
    pattern,
    enemyType,
    duration = null,
    ...args
  ) {
    const event = {
      triggerTime: this.gameTime + delaySeconds,
      interval: intervalSeconds,
      pattern: pattern,
      enemyType: enemyType,
      args: args,
      repeat: true,
      duration: duration, // duration 추가 (null이면 무한 반복)
      endTime: duration ? this.gameTime + delaySeconds + duration : null,
      id: this.generateEventId(),
    };

    this.scheduledEvents.push(event);
    const durationText = duration ? ` (${duration}초간)` : " (무한 반복)";
    Utils.log(
      `반복 스폰 스케줄 등록: ${delaySeconds}초 후 시작, ${intervalSeconds}초마다 ${pattern} ${enemyType}${durationText}`
    );

    return event.id;
  }

  // 모든 스케줄된 이벤트 취소
  clearAllEvents() {
    this.scheduledEvents = [];
    Utils.log("모든 스폰 이벤트가 취소되었습니다!");
  }

  // 한국어 주석: 진행 중인 반복 스폰 이벤트만 취소 (보스 등장 시 고급 웨이브 등 종료)
  cancelRepeatingSpawns() {
    const cancelledCount = this.scheduledEvents.filter(
      (event) => event.repeat === true
    ).length;

    // 반복 이벤트만 제거
    this.scheduledEvents = this.scheduledEvents.filter(
      (event) => event.repeat !== true
    );

    if (cancelledCount > 0) {
      Utils.log(
        `보스 등장으로 인해 ${cancelledCount}개의 반복 스폰 이벤트가 취소되었습니다.`
      );
    }
  }

  // 이벤트 ID 생성
  generateEventId() {
    return (
      "spawn_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9)
    );
  }

  // 웨이브 시스템 - 여러 스폰 패턴을 조합
  scheduleWave(waveConfig, duration = 60) {
    const waveEvents = [];

    waveConfig.spawns.forEach((spawn) => {
      // 웨이브 duration 동안 interval 간격으로 반복 스폰
      const eventId = this.scheduleRepeatingSpawn(
        0, // 즉시 시작
        spawn.interval,
        spawn.pattern,
        spawn.enemyType,
        duration, // duration 전달
        ...(spawn.args || [])
      );
      waveEvents.push(eventId);
    });

    Utils.log(
      `웨이브 스케줄 등록: ${waveConfig.spawns.length}개 반복 스폰 이벤트 (${duration}초간)`
    );
    return waveEvents;
  }

  // 특정 기준 지연 시간에 웨이브를 스케줄링 (반복 스폰)
  scheduleWaveAt(baseDelaySeconds, waveConfig, duration = 60) {
    // 한국어 주석: baseDelaySeconds 후에 웨이브 duration 동안 interval 간격으로 반복 스폰
    const waveEvents = [];
    waveConfig.spawns.forEach((spawn) => {
      const eventId = this.scheduleRepeatingSpawn(
        baseDelaySeconds, // 웨이브 시작 지연
        spawn.interval, // 반복 간격
        spawn.pattern,
        spawn.enemyType,
        duration, // duration 전달
        ...(spawn.args || [])
      );
      waveEvents.push(eventId);
    });
    return waveEvents;
  }

  // 타임라인 시작: config에 명시된 startAt/duration에 맞춰 웨이브들을 실행
  startTimeline(timeline) {
    Utils.log("SpawnScheduler.startTimeline() 호출됨");
    Utils.log("받은 타임라인:", timeline);

    if (!timeline || !Array.isArray(timeline) || timeline.length === 0) {
      Utils.log("유효하지 않은 타임라인입니다!", "error");
      return;
    }

    // 한국어 주석: 기존 이벤트를 지우고 시간 0부터 다시 시작
    this.clearAllEvents();
    this.start();

    Utils.log("기존 이벤트 제거 및 스케줄러 시작 완료");

    // 한국어 주석: 타임라인 항목 순회하면서 각 웨이브를 지정된 시간에 스케줄
    timeline.forEach((segment) => {
      Utils.log(`타임라인 세그먼트 처리 중:`, segment);

      if (!segment || !segment.wave) {
        Utils.log("유효하지 않은 세그먼트입니다:", segment, "error");
        return;
      }

      const wave = EnemySpawnConfig.getWaveConfig(segment.wave);
      if (!wave) {
        Utils.log(`웨이브 설정을 찾을 수 없음: ${segment.wave}`, "error");
        return;
      }

      Utils.log(`웨이브 설정 찾음:`, wave);

      // 한국어 주석: segment.startAt 초 후 웨이브 이벤트 묶음 등록 (duration 동안 반복)
      const eventIds = this.scheduleWaveAt(
        segment.startAt || 0,
        wave,
        segment.duration || 60
      );
      Utils.log(
        `${segment.wave} 웨이브가 ${segment.startAt || 0}초 후 시작됩니다. (${
          segment.duration || 60
        }초간 반복, 이벤트 ID: ${eventIds.length}개)`
      );
      // 한국어 주석: duration은 시각적/논리적 구분용이며, 별도 종료 로직은 필요 시 추가 가능
    });

    Utils.log(
      "웨이브 타임라인이 시작되었습니다 (BEGINNER→INTERMEDIATE→ADVANCED 각 60초)"
    );
  }

  // 스테이지 시작 (새로운 스테이지 시스템)
  startStage(stageNumber = 0) {
    Utils.log(
      `=== 스테이지 ${stageNumber + 1} 시작 (게임 시간: ${this.gameTime.toFixed(
        1
      )}초) ===`
    );

    // 한국어 주석: 스테이지 시작 표시
    const displayStageNumber = stageNumber + 1;
    const subtitles = [
      "해골들을 물리쳐라!",
      "오늘 저녁은 해골탕",
      "동료들의 희생... 잊지 않겠어...",
      "여기가 한계인까...?",
    ];
    const subtitle =
      subtitles[Math.min(stageNumber, subtitles.length - 1)] ||
      "이제 죽을 때가 되지 않았니?";

    // 게임 인스턴스를 통해 UI 매니저 접근 (this.game 또는 window.game 사용)
    const gameInstance = this.game || window.game;
    if (gameInstance && gameInstance.uiManager) {
      gameInstance.uiManager.showStageStart(displayStageNumber, subtitle);
    }

    this.currentStage = stageNumber;
    this.stageStartTime = this.gameTime;
    this.isBossWave = false;
    this.currentBoss = null;
    this.currentBosses = []; // 여러 보스 지원을 위한 배열 초기화

    // 기존 이벤트 제거
    this.clearAllEvents();

    // 스테이지 타임라인 가져오기
    const stageTimeline = EnemySpawnConfig.getStageTimeline();

    // 각 웨이브를 스테이지 시작 시간 기준으로 스케줄
    // segment.startAt은 스테이지 내에서의 상대 시간이므로,
    // stageStartTime + segment.startAt을 기준으로 절대 시간 계산
    stageTimeline.forEach((segment) => {
      const wave = EnemySpawnConfig.getWaveConfig(segment.wave);
      if (!wave) {
        Utils.log(`웨이브 설정을 찾을 수 없음: ${segment.wave}`, "error");
        return;
      }

      // 스테이지 시작 후 상대 시간을 절대 시간으로 변환
      const absoluteStartTime = this.stageStartTime + segment.startAt;
      const delaySeconds = absoluteStartTime - this.gameTime;

      // 지연 시간이 음수면 이미 지난 시간이므로 스킵 (스테이지 전환 등의 경우)
      if (delaySeconds < 0) {
        Utils.log(
          `웨이브 ${
            segment.wave
          } 스킵: 이미 지난 시간입니다 (${delaySeconds.toFixed(1)}초 전)`,
          "warn"
        );
        return;
      }

      if (segment.wave === "BOSS") {
        // 보스 웨이브는 특별 처리 (스테이지 시작 후 segment.startAt초 후 소환)
        // boss 패턴을 사용하면 보스 1마리 생성
        this.scheduleSpawn(delaySeconds, "boss", "WARRIOR");
        Utils.log(
          `보스 웨이브 스케줄: ${delaySeconds.toFixed(
            1
          )}초 후 (절대 시간: ${absoluteStartTime.toFixed(
            1
          )}초) WARRIOR 보스 1마리 소환 (스테이지 ${stageNumber + 1})`
        );

        // 보스 등장 5초 전부터 카운트다운 표시 (5, 4, 3, 2, 1)
        const bossSpawnTime = absoluteStartTime;

        // 각 카운트다운 숫자마다 스케줄 (5, 4, 3, 2, 1)
        for (let countdown = 5; countdown >= 1; countdown--) {
          const countdownTime = bossSpawnTime - countdown;
          const countdownDelay = countdownTime - this.gameTime;

          // 이미 지난 시간이면 스킵
          if (countdownDelay < 0) {
            Utils.log(
              `카운트다운 ${countdown} 스킵: 이미 지난 시간 (${countdownDelay.toFixed(
                1
              )}초 전)`,
              "warn"
            );
            continue;
          }

          // 카운트다운 표시 이벤트 스케줄
          this.scheduledEvents.push({
            triggerTime: countdownTime,
            pattern: "boss_countdown",
            enemyType: null,
            countdown: countdown,
            repeat: false,
            id: this.generateEventId(),
          });

          Utils.log(
            `보스 카운트다운 ${countdown} 스케줄: ${countdownDelay.toFixed(
              1
            )}초 후 (절대 시간: ${countdownTime.toFixed(1)}초)`
          );
        }
      } else {
        // 일반 웨이브는 duration 동안 반복 스폰
        // duration도 스테이지 시작 시간 기준으로 계산해야 함
        const absoluteEndTime = segment.duration
          ? absoluteStartTime + segment.duration
          : null;
        const durationFromNow = absoluteEndTime
          ? absoluteEndTime - this.gameTime
          : null;

        // duration이 0 이하면 스킵
        if (durationFromNow !== null && durationFromNow <= 0) {
          Utils.log(
            `웨이브 ${segment.wave} 스킵: duration이 이미 지났습니다`,
            "warn"
          );
          return;
        }

        // 한국어 주석: durationFromNow는 현재 시점 기준 잔여 시간이므로 여기선 사용하면 안 됨
        // scheduleWaveAt 내부에서 endTime을 this.gameTime + delay + duration으로 계산하므로
        // 의도된 웨이브 지속시간(segment.duration) 자체를 그대로 넘겨야 함 (겹침 방지)
        const eventIds = this.scheduleWaveAt(
          delaySeconds,
          wave,
          segment.duration
        );
        Utils.log(
          `${segment.wave} 웨이브 스케줄: ${delaySeconds.toFixed(
            1
          )}초 후 시작 (절대 시간: ${absoluteStartTime.toFixed(1)}초), ${
            durationFromNow?.toFixed(1) || "무한"
          }초간 (이벤트 ${eventIds.length}개)`
        );
      }
    });
  }

  // 다음 스테이지로 전환
  nextStage() {
    const nextStageNumber = this.currentStage + 1;
    Utils.log(`\n>>> 다음 스테이지로 전환: ${nextStageNumber + 1} <<<\n`);

    // 게임 시간을 유지하면서 스테이지만 전환
    this.startStage(nextStageNumber);
  }

  // 보스 생성 시 호출 (보스 참조 저장)
  registerBoss(boss) {
    if (!this.currentBosses) {
      this.currentBosses = [];
    }
    this.currentBosses.push(boss);
    this.isBossWave = true;
    // 하위 호환성을 위해 마지막 보스를 currentBoss에도 저장
    this.currentBoss = boss;
    Utils.log(
      `보스 등록됨: ${boss.enemyType} (총 ${this.currentBosses.length}마리)`
    );
  }

  // 보스 처치 확인 (매 프레임 호출)
  checkBossDeath() {
    if (!this.isBossWave) return;

    // 여러 보스 지원
    if (this.currentBosses && this.currentBosses.length > 0) {
      // 살아있는 보스만 필터링
      this.currentBosses = this.currentBosses.filter((boss) => {
        if (boss && boss.isAlive) {
          return true;
        }
        // 죽는 애니메이션 중인 보스는 제외하지 않음 (완전히 사라질 때까지 대기)
        if (boss && boss.isDying) {
          return true;
        }
        return false;
      });

      // 모든 보스가 죽었는지 확인
      if (this.currentBosses.length === 0) {
        Utils.log("모든 보스가 처치되었습니다! 다음 스테이지로 넘어갑니다.");
        this.isBossWave = false;
        this.currentBoss = null;
        this.currentBosses = [];

        // 다음 스테이지 시작
        this.nextStage();
      }
    } else if (this.currentBoss) {
      // 하위 호환: 단일 보스 처리
      if (!this.currentBoss.isAlive && !this.currentBoss.isDying) {
        Utils.log("보스가 처치되었습니다! 다음 스테이지로 넘어갑니다.");
        this.isBossWave = false;
        this.currentBoss = null;

        // 다음 스테이지 시작
        this.nextStage();
      }
    }
  }

  // 현재 스케줄러 상태 정보
  getStatus() {
    return {
      isActive: this.isActive,
      gameTime: this.gameTime,
      currentStage: this.currentStage + 1, // 1부터 시작하도록 표시
      isBossWave: this.isBossWave,
      scheduledEventsCount: this.scheduledEvents.length,
      nextEvent:
        this.scheduledEvents.length > 0
          ? Math.min(...this.scheduledEvents.map((e) => e.triggerTime))
          : null,
      upcomingEvents: this.scheduledEvents
        .filter((e) => e.triggerTime > this.gameTime)
        .sort((a, b) => a.triggerTime - b.triggerTime)
        .slice(0, 5) // 다음 5개 이벤트만
        .map((e) => ({
          timeLeft: e.triggerTime - this.gameTime,
          pattern: e.pattern,
          enemyType: e.enemyType,
          repeat: e.repeat,
        })),
    };
  }
}

// webpack 빌드를 위해 default export 추가
export default SpawnScheduler;
